import { MAX_WORK_ITEM_PRIORITY, type WorkItemDetail } from '@deskworks/shared-types';
import { useEffect, useState, type ReactNode } from 'react';
import { NONE } from '@/components/desk-dialogs';
import { Field } from '@/components/field';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { useAgents, useApiMutation } from '@/lib/hooks';
import { t } from '@/lib/i18n';
import { parsePriority } from '@/lib/labels';
import { qk } from '@/lib/query-keys';

export function EditWorkItemDialog({ item, children }: { item: WorkItemDetail; children: ReactNode }) {
  const agents = useAgents(item.workspaceId);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(item.title);
  const [description, setDescription] = useState(item.description);
  const [goal, setGoal] = useState(item.goal);
  const [approvalRule, setApprovalRule] = useState(item.approvalRule ?? '');
  const [priority, setPriority] = useState(String(item.priority));
  const [agentId, setAgentId] = useState(item.assignedAgentId ?? NONE);
  const [dueAt, setDueAt] = useState(toLocalInput(item.dueAt));

  useEffect(() => {
    if (!open) return;
    setTitle(item.title);
    setDescription(item.description);
    setGoal(item.goal);
    setApprovalRule(item.approvalRule ?? '');
    setPriority(String(item.priority));
    setAgentId(item.assignedAgentId ?? NONE);
    setDueAt(toLocalInput(item.dueAt));
  }, [open, item.title, item.description, item.goal, item.approvalRule, item.priority, item.assignedAgentId, item.dueAt]);

  const save = useApiMutation(
    () =>
      api.workItems.update(item.id, {
        title: title.trim(),
        description,
        goal,
        approvalRule: approvalRule.trim() || null,
        priority: parsePriority(priority),
        assignedAgentId: agentId === NONE ? null : agentId,
        dueAt: dueAt ? new Date(dueAt).toISOString() : null,
      }),
    {
      success: t('Saved'),
      invalidate: [qk.workItem(item.id), qk.workItemsAll(item.workspaceId), qk.desk(item.deskId)],
      onSuccess: () => setOpen(false),
    },
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-2xl">
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (title.trim()) save.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle>{t('Edit Work Item')}</DialogTitle>
          </DialogHeader>
          <Field label={t('Title')}>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} required />
          </Field>
          <Field label={t('Description')} hint={t('What should be done, with all context.')} optional>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={8} />
          </Field>
          <Field label={t('Goal')} hint={t('When is it done?')} optional>
            <Textarea value={goal} onChange={(e) => setGoal(e.target.value)} rows={2} />
          </Field>
          <Field label={t('Ask for approval')} hint={t('Where should the agent stop and ask you before it continues? Before that point it works on its own. A running agent is told about changes right away.')} optional>
            <Textarea
              value={approvalRule}
              onChange={(e) => setApprovalRule(e.target.value)}
              rows={2}
              placeholder={t('e.g. Analysing the case and the code is fine. Before you change code or write to the customer, ask me.')}
            />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('Agent')}>
              <Select value={agentId} onValueChange={setAgentId}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>{t('No agent')}</SelectItem>
                  {(agents.data ?? []).map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label={t('Priority')} hint={t('0–100, 50 = normal. Higher is worked on first.')}>
              <Input type="number" value={priority} onChange={(e) => setPriority(e.target.value)} min={0} max={MAX_WORK_ITEM_PRIORITY} step={1} />
            </Field>
          </div>
          <Field label={t('Due')} optional hint={t("When it passes, the Lead is woken (for a sub-item: the parent's Lead).")}>
            <Input type="datetime-local" value={dueAt} onChange={(e) => setDueAt(e.target.value)} />
          </Field>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              {t('Cancel')}
            </Button>
            <Button type="submit" loading={save.isPending} disabled={!title.trim()}>
              {t('Save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** An ISO date as the value of a datetime-local input (local time, minutes). */
const toLocalInput = (v: string | null) => (v ? new Date(new Date(v).getTime() - new Date(v).getTimezoneOffset() * 60000).toISOString().slice(0, 16) : '');
